import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Button } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { SnackbarProvider } from 'notistack';
import { useOfflineStore } from './utils/offline/offline-state-manager';
import {
  OfflineIndicator,
  OfflineSettings,
} from './components/offline';
import AuthErrorToast from './components/AuthErrorToast';
import CookieConsent from './components/CookieConsent/CookieConsent';
import cookieManager from './utils/cookieManager';

// Import existing pages
import ChatPage from './pages/Chat';
import AppStorePage from './pages/AppStore';
import ContextDashboard from './pages/ContextDashboard';
import KnowledgeGraphPage from './pages/KnowledgeGraph/KnowledgeGraphPage';
import DecisionPage from './pages/Decision/DecisionPage';
import SynthesisPage from './pages/Synthesis/SynthesisPage';
import EvolutionPage from './pages/Evolution/EvolutionPage';
import { SubscriptionPage, TokenUsagePage } from './pages/Billing';
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import OAuthCallback from './pages/Auth/OAuthCallback';
import AcceptInvitation from './pages/Auth/AcceptInvitation';
import PricingPage from './pages/Pricing/PricingPage';
import { TermsOfService, PrivacyPolicy } from './pages/Legal';
import { TaskManagementPage, KnowledgeManagementPage, AITeammatesPage } from './pages/Features';
import TasksPage from './pages/Tasks/TasksPage';
// import ProjectsPage from './pages/Projects/ProjectsPage'; // Commented out for workflow automation
import WorkflowsPage from './pages/Workflows/WorkflowsPage';
import { DocsPage } from './pages/Documentation';
import { MarketingPage } from './pages/Home';
import { FieldDemoPage } from './pages/Field';
import { ContactPage } from './pages/Contact';
// import { MethodologyDashboard } from './components/Methodologies'; // Commented out for workflow automation
import { useAuthStore } from './store/authStore';
import { BillingSetup } from './components/Billing/BillingSetup';
import BillingComplete from './pages/Billing/BillingComplete';
import { RequireSubscription } from './components/RequireSubscription';
import { TokenExpirationMonitor } from './components/Auth';
import { GlobalErrorHandler } from './components/ErrorBoundary/GlobalErrorHandler';
import { UpdateNotification } from './components/UpdateNotification';
import { useCacheManager } from './hooks/useCacheManager';
import { isTokenExpired } from './utils/tokenValidator';

// Create Prismatic Forge theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5B8FDE', // Brighter blue (was #325BA8)
      light: '#7BA8E8', // Even lighter for hover states
      dark: '#4A7BC5', // Slightly darker for pressed states
    },
    secondary: {
      main: '#4DC4D2',
    },
    error: {
      main: '#E94257',
    },
    background: {
      default: '#0C1319',
      paper: '#1A1F26',
    },
    text: {
      primary: '#F8F8F8',
      secondary: '#999999',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, initialize } = useAuthStore();
  const [isReady, setIsReady] = React.useState(false);
  const [isValid, setIsValid] = React.useState(true);
  
  React.useEffect(() => {
    // Call initialize to ensure auth state is loaded
    initialize();
    
    // Check token validity
    const checkToken = () => {
      const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (currentToken) {
        if (isTokenExpired(currentToken)) {
          setIsValid(false);
          // Clear auth data
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          
          // Dispatch auth error event
          const event = new CustomEvent('auth-error', {
            detail: { message: 'Your session has expired. Please log in again.' }
          });
          window.dispatchEvent(event);
        }
      }
    };
    
    // Check token immediately
    checkToken();
    
    // Give the auth store a moment to initialize from localStorage
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [initialize]);
  
  if (!isReady) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated || !isValid) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Component to conditionally show header based on route
const AppContent: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuthStore();
  const { conflictCount } = useOfflineStore();
  
  // Check if current route should show the global header
  const shouldShowHeader = isAuthenticated && !location.pathname.startsWith('/chat');
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: '100vw',
      overflow: 'auto'
    }}>
      {/* Header with offline indicator - only show when authenticated and not on chat page */}
      {shouldShowHeader && (
        <header style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 16,
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            Prismatic Forge
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <OfflineIndicator />
            {conflictCount > 0 && (
              <span style={{ color: '#ed6c02' }}>
                {conflictCount} conflicts
              </span>
            )}
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                  {user.email}
                </span>
                <Button
                  size="small"
                  startIcon={<LogoutIcon />}
                  onClick={() => {
                    logout();
                    window.location.href = '/login';
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Logout
                </Button>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main content area */}
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        width: '100%'
      }}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={isAuthenticated ? <Navigate to="/chat" replace /> : <MarketingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/auth/callback/microsoft" element={<OAuthCallback />} />
          <Route path="/auth/callback/microsoft_exchange" element={<OAuthCallback />} />
          <Route path="/auth/callback/gmail" element={<OAuthCallback />} />
          <Route path="/auth/callback/google" element={<OAuthCallback />} />

          {/* Field spatial demo — public */}
          <Route path="/field" element={<FieldDemoPage />} />

          {/* Legal pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Feature pages */}
          <Route path="/features/task-management" element={<TaskManagementPage />} />
          <Route path="/features/knowledge-management" element={<KnowledgeManagementPage />} />
          <Route path="/features/ai-teammates" element={<AITeammatesPage />} />

          {/* Contact */}
          <Route path="/contact" element={<ContactPage />} />

          {/* Documentation pages */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:section" element={<DocsPage />} />
          <Route path="/docs/:section/:subsection" element={<DocsPage />} />
          <Route path="/docs/:section/:subsection/:item" element={<DocsPage />} />

          {/* Protected routes */}
          <Route path="/chat" element={
            <ProtectedRoute>
              <RequireSubscription>
                <ChatPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/tasks" element={
            <ProtectedRoute>
              <RequireSubscription>
                <TasksPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          {/* TODO: Project Management - Commented out for workflow automation replacement */}
          {/*
          <Route path="/projects" element={
            <ProtectedRoute>
              <RequireSubscription>
                <ProjectsPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/projects/new" element={
            <ProtectedRoute>
              <RequireSubscription>
                <ProjectsPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/projects/:id/edit" element={
            <ProtectedRoute>
              <RequireSubscription>
                <ProjectsPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/methodologies" element={
            <ProtectedRoute>
              <RequireSubscription>
                <MethodologyDashboard />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          */}

          {/* NEW: Workflow Automation Routes */}
          <Route path="/workflows" element={
            <ProtectedRoute>
              <RequireSubscription>
                <WorkflowsPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/chat/:conversationId" element={
            <ProtectedRoute>
              <RequireSubscription>
                <ChatPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/apps" element={
            <ProtectedRoute>
              <AppStorePage />
            </ProtectedRoute>
          } />
          <Route path="/context" element={
            <ProtectedRoute>
              <ContextDashboard />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-graph" element={
            <ProtectedRoute>
              <KnowledgeGraphPage />
            </ProtectedRoute>
          } />
          <Route path="/decision" element={
            <ProtectedRoute>
              <RequireSubscription>
                <DecisionPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/synthesis" element={
            <ProtectedRoute>
              <RequireSubscription>
                <SynthesisPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/evolution" element={
            <ProtectedRoute>
              <RequireSubscription>
                <EvolutionPage />
              </RequireSubscription>
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          } />
          <Route path="/billing/tokens" element={
            <ProtectedRoute>
              <TokenUsagePage />
            </ProtectedRoute>
          } />
          <Route path="/billing/setup" element={
            <ProtectedRoute>
              <BillingSetup />
            </ProtectedRoute>
          } />
          <Route path="/billing/setup/complete" element={
            <ProtectedRoute>
              <BillingComplete />
            </ProtectedRoute>
          } />
          <Route path="/settings/offline" element={
            <ProtectedRoute>
              <OfflineSettings />
            </ProtectedRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to={isAuthenticated ? "/chat" : "/"} replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  const {
    initialize,
    destroy,
  } = useOfflineStore();
  const { initialize: initAuth } = useAuthStore();
  const { cacheManager } = useCacheManager();

  // Initialize auth on first render
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    // Initialize offline functionality
    initialize();

    return () => {
      destroy();
    };
  }, [initialize, destroy]);

  // Initialize cache management
  useEffect(() => {
    cacheManager.setupAutoInvalidation();
  }, [cacheManager]);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <CssBaseline />
        <Router>
        {/* Token Expiration Monitor - Global token expiration checking */}
        <TokenExpirationMonitor />

        {/* Global Error Handler - Shows auth and app errors as snackbars */}
        <GlobalErrorHandler />

        {/* Cookie Consent Popup */}
        <CookieConsent
          onAccept={(preferences) => {
            cookieManager.updatePreferences(preferences);
          }}
          onDecline={() => {
            // Non-essential cookies declined
          }}
        />

        {/* App content with conditional header */}
        <AppContent />

        {/* Global components */}
        <AuthErrorToast />
        <UpdateNotification />
      </Router>
    </SnackbarProvider>
  </ThemeProvider>
  );
}

export default App;